import Triggers from '../collections/Triggers';
import Trigger from '../models/Trigger';
import Contacts from '../collections/Contacts';
import { debounce } from 'lodash';
import { baseApi } from 'core/api';

const state = () => ({
    _item: null,
    _items: [],
    _error: false,
    _triggersLoading: false,
    _more: null,
    _total: 0,
    _search: {
        items: [],
        more: null,
        total: 0,
        term: null,
        pagination: null,
    },
    _pagination: null,
    _parameters: {
        page: 1,
        sortBy: null,
        sortOrder: null,
        search: null,
        length: 10,
        filter: null,
        filtersList: [],
    },
    _contactsParameters: {
        page: 1,
        sortBy: 'created_at',
        sortOrder: 'desc',
        search: null,
        length: 10,
        filter: null,
    },
    _contactsItems: null,
    _contactsPagination: null,
});

const getters = {
    triggers: (state) => {
        if (state._search.term !== null) {
            return state._search.items;
        } else {
            return state._items;
        }
    },
    triggersPagination: (state) => {
        if (state._search.term !== null) {
            return state._search.pagination;
        } else {
            return state._pagination;
        }
    },
    triggersTotal: (state) => {
        if (state._search.term !== null) {
            return state._search.total;
        } else {
            return state._total;
        }
    },
    triggersHaveMore: (state) => {
        if (state._search.term !== null) {
            return state._search.more !== null;
        } else {
            return state._more !== null;
        }
    },
    triggersSearchTerm: (state) => state._search.term,
    triggersError: (state) => state._error,
    triggersLoading: (state) => state._triggersLoading,
    triggersFilters: (state) => state._parameters.filtersList,

    contactsTrigger: (state) => state._contactsItems,
    contactsTriggerPagination: (state) => state._contactsPagination,
    contactsTriggerParameters: (state) => state._contactsParameters,
    triggersSort: (state) => ({
        sortOrder: state._parameters.sortOrder,
        sortBy: state._parameters.sortBy,
    }),
};

const utils = {
    triggersLoad: (storeInstance, retry) => {
        let url = 'core/triggers';

        if (!storeInstance._more) {
            storeInstance._setTriggersLoading(true);
        }

        if (!retry && storeInstance._more) {
            url = storeInstance._more;
        } else if (retry === true) {
            storeInstance._setTriggersRetryLoading();
        }

        let sortType = null;

        if (
            ['total_count', 'success_count', 'reply_count'].includes(
                storeInstance._parameters.sortBy
            )
        ) {
            sortType = 'daily_trigger_statistics';
        }

        if (['morphClicks', 'morphSentBy'].includes(storeInstance._parameters.sortBy)) {
            sortType = 'morph';
        }

        return baseApi
            .get(url, { params: { ...storeInstance._parameters, sortType } })
            .then((response) => {
                storeInstance._setTriggersLoaded({
                    data: response.data,
                    rootGetters: storeInstance.rootGetters,
                });
                storeInstance._setTriggersLoading(false);

                return response;
            })
            .catch((e) => {
                storeInstance._setTriggersErrorLoading();
                storeInstance._setTriggersLoading(false);

                return e;
            });
    },
};

const debounceContactsTriggerLoad = debounce((storeInstance, action) => {
    storeInstance[action]();
}, 500);
const debounceTriggersLoad = debounce(utils.triggersLoad, 500);

const actions = {
    /**
     * Loads the triggers from the backend.
     *
     * @param {Boolean} retry
     * @return {Promise}
     */
    triggersLoad(retry) {
        if (!this._more) {
            this._setTriggersLoading(true);
        }

        return debounceTriggersLoad(this, retry);
    },

    /**
     * Search the triggers.
     *
     * @param {String} searchTerm
     * @param {Boolean} isLoadMore
     * @return {Promise}
     */
    triggersSearch({ searchTerm, isLoadMore = false }) {
        this._setTriggersLoading(true);
        this._setTriggersChangeSort(null);

        return baseApi
            .get('core/triggers/search', { params: { term: searchTerm, ...this._parameters } })
            .then((response) => {
                this._setTriggersSearchLoaded({
                    term: searchTerm,
                    data: response.data,
                    rootGetters: this.rootGetters,
                });

                this._setTriggersLoading(false);
                return response;
            })
            .catch((e) => {
                this._setTriggersSearchError();
                this._setTriggersLoading(false);

                return e;
            });
    },

    setTriggersFilters() {
        return baseApi
            .get('core/triggers/filters', {
                params: {
                    withCustomFields: true,
                },
            })
            .then((response) => {
                return response.data;
            });
    },

    async triggersFiltersApply(filters) {
        this._setTriggersFiltersApply(filters);
        return this.triggersLoad();
    },

    /**
     * Clears the search results.
     *
     * @return {void}
     */
    triggersClearSearch() {
        this._setTriggersUpdatePage(1);
        this._setTriggersSearchCleared();
        this.triggersLoad();
    },

    /**
     * Creates a trigger using the given form.
     *
     * @param {Form} form
     * @return {Promise}
     */
    triggersCreate(form) {
        return form
            .post('core/triggers')
            .then((response) => {
                this._setTriggerCreated(response.data);
                return response;
            })
            .finally(() => {
                this.triggersLoad();
            });
    },

    /**
     * Updates a trigger using the given form.
     *
     * @param {Number} id
     * @param {Form} form
     * @return {Promise}
     */
    triggersUpdate({ id, form }) {
        return form.put(`core/triggers/${id}`).then((response) => {
            this._setTriggerUpdated(response.data);
            return response;
        });
    },

    /**
     * Deletes the given trigger.
     *
     * @param {Number} id
     * @param {Form} form
     * @return {Promise}
     */
    triggersDelete({ id, form }) {
        return form
            .delete(`core/triggers/${id}`)
            .then((response) => {
                this._setTriggerDeleted(id);
                return response;
            })
            .finally(() => {
                this.triggersLoad();
            });
    },

    /**
     * Bulk deletes the given triggers.
     *
     * @param {Form} form
     * @return {Promise}
     */
    triggersBulkDelete(form) {
        return form
            .delete('core/triggers')
            .then((response) => {
                this._setTriggersBulkDeleted(form.ids);
                return response;
            })
            .finally(() => {
                this.triggersLoad();
            });
    },

    /**
     * Adds the given trigger to the stack.
     *
     * @param {Object} trigger
     * @return {void}
     */
    triggerCreated(trigger) {
        this._setTriggerCreated(trigger);
    },

    triggersReset() {
        this._setTriggersReset();
        this._setTriggersResetParameters();
    },

    /**
     * Triggered when a trigger is updated.
     *
     * @param {Object} trigger
     * @return {void}
     */
    triggerUpdated(trigger) {
        this._setTriggerUpdated(trigger);
        debounceContactsTriggerLoad(this, 'contactsTriggerLoad');
    },

    triggersUpdateTerm({ searchTerm }) {
        this._setTriggerUpdateTerm(searchTerm);
    },

    async triggersUpdatePage(page) {
        this._setTriggersUpdatePage(page);

        if (this.triggersSearchTerm) {
            this.triggersSearch({ searchTerm: this.triggersSearchTerm });
        } else {
            this.triggersLoad();
        }
    },

    async triggersUpdatePageValue(page) {
        this._setTriggersUpdatePage(page);
    },

    async triggersUpdateLength(page) {
        this._setTriggersUpdateLength(page);
        this.triggersLoad();
    },

    triggersCustomUpdateLengthSelector({ page, data }) {
        this._setTriggersUpdateLength(page);
        this.triggersUpdatePage(data);
    },

    /**
     * Removes the given trigger ID from the list.
     *
     * @param {Number} id
     * @return {void}
     */
    triggerDeleted({ id }) {
        this._setTriggerDeleted(id);
    },

    /**
     * Removes the given trigger IDs from the list.
     *
     * @param {Number} ids
     * @return {void}
     */
    triggersBulkDeleted(ids) {
        this._setTriggersBulkDeleted(ids);
    },

    /**
     * Toggles a trigger
     *
     * @param {Number} id
     * @return {Promise}
     */
    triggersToggle(id) {
        return baseApi.put(`core/triggers/toggle/${id}`).then((response) => {
            this._setTriggerUpdated(response.data);
            return response;
        });
    },

    triggerById(id) {
        return baseApi.get(`core/triggers/${id}`).then((response) => {
            return new Trigger(response.data);
        });
    },

    contactsTriggerLoad(retry) {
        if (retry === true) {
            this._setContactsTriggerRetryLoading();
        }

        return baseApi
            .get(`core/triggers/contacts/${this._item.id}`, {
                params: this._contactsParameters,
            })
            .then((response) => {
                this._setContactsTriggerLoaded(response.data);
                return response;
            })
            .catch(() => {
                this._setContactsTriggerErrorLoading();
            });
    },

    contactsTriggerUpdateFilter({ filter, request = true }) {
        this._setContactsTriggerUpdateFilter(filter);

        if (request) {
            debounceContactsTriggerLoad(this, 'contactsTriggerLoad');
        }
    },

    triggerChoose(broadcast) {
        this._setTriggerChoose(broadcast);
    },

    contactsTriggerUpdatePage(page) {
        this._setContactsTriggerUpdatePage(page);
        debounceContactsTriggerLoad(this, 'contactsTriggerLoad');
    },

    triggerCustomUpdateLengthSelector({ page, data }) {
        this._setTriggersUpdateLength(page);
        this.triggersUpdatePage(data);
    },

    contactsTriggerUpdateLength(length) {
        this._setContactsTriggerUpdateLength(length);
        debounceContactsTriggerLoad(this, 'contactsTriggerLoad');
    },

    contactsTriggerUpdateSearch(search) {
        this._setContactsTriggerUpdateSearch(search);
        debounceContactsTriggerLoad(this, 'contactsTriggerLoad');
    },

    exportTriggerById({ id, filter1 }) {
        return baseApi.get(`core/triggers/contacts/${id}/export`, {
            params: {
                filter: filter1,
            },
        });
    },

    triggerUpdateState(props) {
        this._setTriggerUpdateState(props);
    },

    changeSort(sortBy) {
        this._setTriggersChangeSort(sortBy);

        if (this._search.term) {
            this.triggersSearch({ searchTerm: this._search.term });
        } else {
            this.triggersLoad();
        }
    },

    testTrigger(form) {
        return form.post('core/triggers/test').then((response) => {
            return response.data;
        });
    },

    // Private methods for all operations
    _setTriggerUpdateState(props) {
        for (let key in props) {
            const mappedKey = key.startsWith('_') ? key : `_${key}`;
            this[mappedKey] = props[key];
        }
    },

    _setTriggerChoose(broadcast) {
        this._item = broadcast;
    },

    _setTriggersFiltersApply(filtersList) {
        this._parameters.filtersList = filtersList;
    },

    _setTriggerCreated(data) {
        this._items.unshift(new Trigger(data));
        ++this._total;
    },

    _setTriggerUpdated(data) {
        this._items = this._items.map((o) => {
            if (o.id === data.id) {
                return new Trigger(data);
            } else {
                return o;
            }
        });

        if (this._search.results) {
            this._search.results = this._search.results.map((o) => {
                if (o.id === data.id) {
                    return new Trigger(data);
                } else {
                    return o;
                }
            });
        }
    },

    _setTriggersReset() {
        this._items = [];
        this._pagination = null;
    },

    _setTriggersResetParameters() {
        this._parameters = {
            page: 1,
            sortBy: null,
            sortOrder: null,
            filtersList: [],
            limit: 10,
        };
    },

    _setTriggersLoaded({ data, rootGetters }) {
        const items = data.data;
        const pagination = data.meta;
        const links = data.links;

        if (this._more) {
            const triggers = new Triggers(
                items,
                {},
                {
                    members: rootGetters.members,
                }
            );

            for (let key in triggers.models) {
                if (key === 'collection') break;
                this._items.push(triggers.models[key]);
            }
        } else {
            const triggers = new Triggers(
                data.data,
                {},
                {
                    members: rootGetters?.members || [],
                }
            );

            this._items = triggers.models;
        }

        this._pagination = {
            current_page: pagination.current_page,
            from: pagination.from,
            last_page: pagination.last_page,
            per_page: pagination.per_page,
            to: pagination.to,
            total: pagination.total,
            next_page_url: links.next,
        };

        this._parameters.page = pagination.current_page;
        this._total = pagination.total;
        this._more = links.next;
    },

    _setTriggersRetryLoading() {
        this._items = [];
        this._more = null;
        this._error = false;
        this._total = 0;
        this._search = {
            items: [],
            more: null,
            total: 0,
            term: null,
        };
    },

    _setTriggersLoading(status = true) {
        this._triggersLoading = status;
    },

    _setTriggersErrorLoading() {
        this._items = [];
        this._more = null;
        this._error = true;
        this._total = 0;
        this._search = {
            items: [],
            more: null,
            total: 0,
            term: null,
        };
    },

    _setTriggerDeleted(id) {
        if (!this._items) return;

        this._items = this._items.filter((o) => o.id !== id);
        --this._total;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => o.id !== id);
        }
    },

    _setTriggersBulkDeleted(ids) {
        if (!this._items) return;

        this._items = this._items.filter((o) => {
            return ids.indexOf(o.id) === -1;
        });

        this._total = this._total - ids.length;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => {
                return ids.indexOf(o.id) === -1;
            });
        }
    },

    _setTriggerUpdateTerm(searchTerm) {
        this._search.term = searchTerm;
    },

    _setTriggersUpdatePage(page) {
        this._parameters.page = page;

        if (this._pagination) {
            this._pagination.current_page = page;
        }
    },

    _setTriggersUpdateLength(page) {
        this._parameters.limit = page;
    },

    _setTriggersSearchLoaded({ term, data, rootGetters }) {
        const items = data.data;
        const pagination = data.meta;
        const links = data.links;
        const triggers = new Triggers(
            items,
            {},
            {
                members: rootGetters.members,
            }
        );

        this._search.items = triggers.models;

        this._search.pagination = {
            current_page: pagination.current_page,
            from: pagination.from,
            last_page: pagination.last_page,
            per_page: pagination.per_page,
            to: pagination.to,
            total: pagination.total,
            next_page_url: links.next,
        };

        this._search.more = links.next;
        this._search.total = pagination.total;
        this._search.term = term;
    },

    _setTriggersSearchError() {
        this._error = true;
        this._search = {
            items: [],
            more: null,
            total: 0,
            term: null,
        };
    },

    _setTriggersSearchCleared() {
        this._error = false;
        this._search = {
            items: [],
            more: null,
            total: 0,
            term: null,
            pagination: null,
        };
    },

    _setContactsTriggerLoaded(data) {
        const contacts = new Contacts(data.data);

        this._contactsItems = contacts.models;
        this._contactsPagination = {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        };
    },

    _setContactsTriggerRetryLoading() {
        this._contactsItems = null;
        this._contactsPagination.total = 0;
    },

    _setContactsTriggerErrorLoading() {
        this._contactsItems = false;
        this._contactsPagination.total = 0;
    },

    _setContactsTriggerUpdatePage(data) {
        this._contactsParameters.page = data;
    },

    _setContactsTriggerUpdateLength(data) {
        this._contactsParameters.length = data;
    },

    _setContactsTriggerUpdateFilter(data) {
        this._contactsParameters.filter = data;
    },

    _setContactsTriggerUpdateSearch(data) {
        this._contactsParameters.search = data;
    },

    _setContactsTriggerSearchCleared() {
        this._contactsParameters.search = null;
    },

    _setTriggersChangeSort(sortBy) {
        if (sortBy === null) {
            this._parameters.sortBy = null;
            this._parameters.sortOrder = null;
            return;
        }

        if (this._parameters.sortBy === sortBy) {
            this._parameters.sortOrder = this._parameters.sortOrder === 'asc' ? 'desc' : 'asc';
        } else {
            this._parameters.sortOrder = 'asc';
        }

        this._parameters.sortBy = sortBy;
    },
};

export default {
    state,
    getters,
    actions,
};
